import React from "react";
import {
  Briefcase,
  Code2,
  FolderOpen,
  Trophy,
  GraduationCap,
  Mail,
  Phone,
  Github,
  Linkedin,
  FileText,
  ExternalLink,
  Lock,
  Award,
  BookOpen,
  Languages,
  Youtube,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { portfolioData } from "@/data/mock";

const iconMap = {
  youtube: Youtube,
  award: Award,
  languages: Languages,
  book: BookOpen,
};

const SectionTitle = ({ icon: Icon, title, withBadge }) => (
  <div className="flex items-center gap-3 mb-8">
    <div className="p-2 bg-[#2563EB]/[0.06] rounded-lg">
      <Icon size={20} className="text-[#2563EB]" />
    </div>
    <h2 className="text-[clamp(1.75rem,3vw,2rem)] font-bold text-[#0A0A0A] dark:text-white tracking-tight">
      {title}
    </h2>
    {withBadge && (
      <span className="ml-1 px-3 py-1 text-xs font-semibold text-[#2563EB] bg-[#2563EB]/[0.08] rounded-full leading-none">
        {withBadge}
      </span>
    )}
  </div>
);

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-16 md:py-24 ${className}`}>
    <div className="max-w-[864px] mx-auto px-4 md:px-6 animate-fade-up">
      {children}
    </div>
  </section>
);

const ExperienceSection = () => (
  <Section id="experience">
    <SectionTitle icon={Briefcase} title="Experiência" />
    <div className="space-y-10">
      {portfolioData.experience.map((job, index) => (
        <div key={index}>
          {index > 0 && <Separator className="mb-10 dark:bg-white/10" />}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-1 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-[#0A0A0A] dark:text-white leading-snug">
                {job.role}
              </h3>
              <p className="text-[#2563EB] font-medium text-[15px]">
                {job.company}
                {job.current && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                    Atual
                  </span>
                )}
              </p>
            </div>
            <div className="text-sm text-[#6B7280] md:text-right flex-shrink-0 mt-1 md:mt-0">
              <p>{job.period}</p>
              <p>{job.location}</p>
            </div>
          </div>

          <ul className="space-y-3 mb-6">
            {job.bullets.map((bullet, i) => (
              <li
                key={i}
                className="flex gap-3 text-[15px] text-[#374151] dark:text-[#D1D5DB] leading-relaxed"
              >
                <span className="w-1.5 h-1.5 bg-[#2563EB] rounded-full mt-[9px] flex-shrink-0" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-1.5">
            {job.skills.map((skill) => (
              <Badge
                key={skill}
                variant="secondary"
                className="text-xs font-medium bg-gray-100 dark:bg-white/[0.08] text-[#374151] dark:text-[#D1D5DB] hover:bg-gray-200 dark:hover:bg-white/[0.12] transition-colors duration-200 cursor-default"
              >
                {skill}
              </Badge>
            ))}
          </div>
        </div>
      ))}
    </div>
  </Section>
);

const SkillsSection = () => (
  <Section id="skills" className="bg-gray-50 dark:bg-white/[0.02]">
    <SectionTitle icon={Code2} title="Skills" />
    <div className="grid gap-8">
      {Object.entries(portfolioData.skills).map(([category, skills]) => (
        <div key={category}>
          <h3 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wider mb-3">
            {category}
          </h3>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill) => (
              <Badge
                key={skill}
                variant="outline"
                className="text-[13px] font-medium text-[#0A0A0A] dark:text-[#E5E7EB] border-gray-200 dark:border-white/[0.12] bg-white dark:bg-white/[0.04] hover:border-[#2563EB] hover:text-[#2563EB] transition-colors duration-200 cursor-default py-1.5 px-3"
              >
                {skill}
              </Badge>
            ))}
          </div>
        </div>
      ))}
    </div>
  </Section>
);

const ProjectsSection = () => (
  <Section id="projects">
    <SectionTitle
      icon={FolderOpen}
      title="Projetos"
      withBadge="Mais recentes"
    />
    <div className="grid md:grid-cols-2 gap-4">
      {portfolioData.projects.map((project, index) => (
        <Card
          key={index}
          className="group border-gray-200 dark:border-white/[0.1] shadow-none hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 bg-white dark:bg-white/[0.03]"
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-2 mb-3">
              <h3 className="text-base font-semibold text-[#0A0A0A] dark:text-white leading-snug">
                {project.title}
              </h3>
              {project.link ? (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 text-[#6B7280] hover:text-[#2563EB] transition-colors duration-200 flex-shrink-0 rounded-md hover:bg-blue-50 dark:hover:bg-blue-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
                  aria-label={`Abrir ${project.title}`}
                >
                  <ExternalLink size={16} />
                </a>
              ) : (
                <div
                  className="p-1.5 text-[#9CA3AF] dark:text-[#6B7280] flex-shrink-0"
                  title="Repositório privado"
                >
                  <Lock size={16} />
                </div>
              )}
            </div>

            <p className="text-sm text-[#6B7280] leading-relaxed mb-4">
              {project.description}
            </p>

            <div className="flex flex-wrap gap-1.5 mb-4">
              {project.tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="text-xs bg-[#2563EB]/[0.06] text-[#2563EB] hover:bg-[#2563EB]/[0.12] transition-colors duration-200 cursor-default"
                >
                  {tag}
                </Badge>
              ))}
            </div>

            {project.link ? (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2563EB] hover:underline underline-offset-2 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] rounded"
              >
                {project.linkLabel}
                <ExternalLink size={14} />
              </a>
            ) : (
              <p className="inline-flex items-center gap-1.5 text-sm text-[#9CA3AF] dark:text-[#6B7280]">
                <Lock size={14} />
                {project.linkLabel}
              </p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  </Section>
);

const ActivitiesSection = () => (
  <Section id="activities" className="bg-gray-50 dark:bg-white/[0.02]">
    <SectionTitle icon={Trophy} title="Atividades" />
    <div className="space-y-4">
      {portfolioData.activities.map((activity, index) => {
        const IconComp = iconMap[activity.icon] || Trophy;
        return (
          <div key={index} className="flex gap-4 items-start">
            <div className="p-2 bg-white dark:bg-white/[0.06] rounded-lg border border-gray-100 dark:border-white/[0.08] flex-shrink-0 mt-0.5 shadow-sm dark:shadow-none">
              <IconComp size={16} className="text-[#2563EB]" />
            </div>
            <p className="text-[15px] text-[#374151] dark:text-[#D1D5DB] leading-relaxed">
              {activity.text}
            </p>
          </div>
        );
      })}
    </div>
  </Section>
);

const EducationSection = () => (
  <Section id="education">
    <SectionTitle icon={GraduationCap} title="Educação" />
    <div className="space-y-4">
      {portfolioData.education.map((edu, index) => (
        <div
          key={index}
          className="p-6 rounded-xl border border-gray-200 dark:border-white/[0.1] bg-white dark:bg-white/[0.03] hover:border-gray-300 dark:hover:border-white/[0.2] transition-colors duration-200"
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-1">
            <div>
              <h3 className="text-base font-semibold text-[#0A0A0A] dark:text-white">
                {edu.degree}
              </h3>
              <p className="text-[15px] text-[#2563EB] font-medium">
                {edu.institution}
              </p>
            </div>
            <div className="flex items-center gap-2 mt-1 md:mt-0 flex-shrink-0">
              <span className="text-sm text-[#6B7280]">{edu.period}</span>
              {edu.highlight && (
                <Badge className="bg-[#2563EB]/[0.08] text-[#2563EB] text-xs font-medium hover:bg-[#2563EB]/[0.12] border-transparent">
                  {edu.highlight}
                </Badge>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </Section>
);

const ContactSection = () => {
  const { personal } = portfolioData;
  return (
    <Section id="contact" className="bg-gray-50 dark:bg-white/[0.02]">
      <SectionTitle icon={Mail} title="Contato" />
      <p className="text-[15px] text-[#6B7280] mb-8 max-w-[540px]">
        Interessado em trabalhar junto ou quer trocar uma ideia? Entre em
        contato por e-mail ou conecte-se pelas redes abaixo.
      </p>

      <div className="space-y-3">
        <a
          href={`mailto:${personal.email}`}
          className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-white/[0.1] bg-white dark:bg-white/[0.03] hover:border-[#2563EB] hover:shadow-sm transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
        >
          <div className="p-2.5 bg-[#2563EB]/[0.06] rounded-lg group-hover:bg-[#2563EB]/[0.12] transition-colors duration-200">
            <Mail size={18} className="text-[#2563EB]" />
          </div>
          <div>
            <p className="text-xs text-[#6B7280] font-medium mb-0.5">E-mail</p>
            <p className="text-sm text-[#0A0A0A] dark:text-white font-medium">
              {personal.email}
            </p>
          </div>
        </a>

        <a
          href={`tel:${personal.phone.replace(/[\s()]/g, "")}`}
          className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-white/[0.1] bg-white dark:bg-white/[0.03] hover:border-[#2563EB] hover:shadow-sm transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
        >
          <div className="p-2.5 bg-[#2563EB]/[0.06] rounded-lg group-hover:bg-[#2563EB]/[0.12] transition-colors duration-200">
            <Phone size={18} className="text-[#2563EB]" />
          </div>
          <div>
            <p className="text-xs text-[#6B7280] font-medium mb-0.5">
              Telefone
            </p>
            <p className="text-sm text-[#0A0A0A] dark:text-white font-medium">
              {personal.phone}
            </p>
          </div>
        </a>

        <div className="flex flex-wrap gap-3 pt-2">
          <a
            href={personal.social.github}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/[0.1] bg-white dark:bg-white/[0.03] hover:border-[#2563EB] hover:shadow-sm transition-all duration-200 text-sm font-medium text-[#0A0A0A] dark:text-white hover:text-[#2563EB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
          >
            <Github size={18} />
            GitHub
          </a>
          <a
            href={personal.social.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/[0.1] bg-white dark:bg-white/[0.03] hover:border-[#2563EB] hover:shadow-sm transition-all duration-200 text-sm font-medium text-[#0A0A0A] dark:text-white hover:text-[#2563EB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
          >
            <Linkedin size={18} />
            LinkedIn
          </a>
          <a
            href={personal.social.cv}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/[0.1] bg-white dark:bg-white/[0.03] hover:border-[#2563EB] hover:shadow-sm transition-all duration-200 text-sm font-medium text-[#0A0A0A] dark:text-white hover:text-[#2563EB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
          >
            <FileText size={18} />
            Currículo
          </a>
        </div>
      </div>
    </Section>
  );
};

const Footer = () => (
  <footer className="py-8 border-t border-gray-100 dark:border-white/[0.06]">
    <div className="max-w-[864px] mx-auto px-4 md:px-6 flex flex-col md:flex-row items-center justify-center gap-3">
      <p className="text-sm text-[#6B7280]">
        &copy; {new Date().getFullYear()} - Guilherme Sanches
      </p>
    </div>
  </footer>
);

export {
  ExperienceSection,
  SkillsSection,
  ProjectsSection,
  ActivitiesSection,
  EducationSection,
  ContactSection,
  Footer,
};
